import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch } from 'react-redux';

import { Icon } from "@iconify/react";
import { Button, Modal } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import PerfectScrollbar from 'react-perfect-scrollbar';

import _ from "lodash";

import { getAllEquipmentUnitCategoriesByProfileId, applyCategoryToEquipmentUnit } from "../../../../api";
import { checkForAPISuccess, showErrorToast, showSuccessToast } from "../../../../Common/commonFunctions";

import { RESPONSE_ERROR_IN_CATEGORY_APPLY_CODE, useQueryConfiguration } from "../../../../Common/constants";
import { DESELECT_COMPONENT, UPDATE_CATEGORY_INFO } from "../../../../store/actions";

const ApplyCategoryToEquipmentUnit = (props) => {

    const dispatch = useDispatch();
    const profileId = window.location.pathname.split("/")[3];
    let updatedTextInputtedComponents = {}, allComponentInputs;

    const [utils, setUtils] = useState({
        categories: {}, selectedCategory: { id: "", name: "" },
        categoryComponents: [], equipmentUnitComponents: [],
        typedInComponents: {}, dropDownSelectedComponents: {},
        existingComponents: {}, invalidTypedInComponentNames: [],
        duplicateComponentNames: [], softDeletedComponents: {}
    });

    const checkIfComponentsAvailableForSelection = (order) => !_.isEmpty(utils.equipmentUnitComponents?.[utils.categoryComponents[order]]);

    const handleComponentSelect = (selectedComponentName, componentOrder) => {
        if(/^([A-Z])\1$/.test(selectedComponentName)) {
            let updatedSelection = _.cloneDeep(utils.dropDownSelectedComponents);
            if(!_.isEmpty(updatedSelection[selectedComponentName])) {
                delete updatedSelection[selectedComponentName];
            }
            setUtils({ 
                ...utils,
                typedInComponents: { ...utils.typedInComponents, ...{ [`${componentOrder}`] : "" } },
                dropDownSelectedComponents: updatedSelection
            });
        }
        else {
            setUtils({ ...utils, dropDownSelectedComponents: { ...utils.dropDownSelectedComponents, ...{ [`${componentOrder}`] : selectedComponentName } } })
        }
    }

    const handleNewComponentInputClose = (componentOrder) => {
        if(Object.keys(utils.typedInComponents)?.includes(componentOrder)) {
            updatedTextInputtedComponents = _.cloneDeep(utils.typedInComponents);
            delete updatedTextInputtedComponents[componentOrder];
            
            setUtils({ ...utils, typedInComponents: updatedTextInputtedComponents });
        }
    }

    const handleNewComponentInput = (componentName, componentOrder) => {
        updatedTextInputtedComponents = _.cloneDeep(utils.typedInComponents);
        updatedTextInputtedComponents[componentOrder] = componentName;
        setUtils({ ...utils, typedInComponents: updatedTextInputtedComponents });
    }

    const handleApplyValidation = () => {
        let invalidComponents = [], existingComponentNames = Object.keys(utils.existingComponents).map(componentName => componentName.toUpperCase());
        Object.values(utils.typedInComponents).forEach(value => {
            if(existingComponentNames.includes(value?.toUpperCase())) {
                if(!invalidComponents.includes(value)) {
                    invalidComponents.push(value);
                }
            }
        });
        if(!_.isEmpty(invalidComponents)) {
            setUtils({ ...utils, invalidTypedInComponentNames: invalidComponents, duplicateComponentNames: [] });
        }
        else {
            let scannedComponents = [], duplicateComponents = [];
            Object.entries({ ...utils.dropDownSelectedComponents, ...utils.typedInComponents }).forEach(([key, value]) => {
                if(scannedComponents.includes(value) && !duplicateComponents.includes(value)) {
                    duplicateComponents.push(value);
                }
                else {
                    scannedComponents.push(value);
                }
            });
            
            if(duplicateComponents.length > 0) {
                setUtils({ ...utils, duplicateComponentNames: duplicateComponents, invalidTypedInComponentNames: [] });
            }
            else {
                let softDeletedComponents = 
                    Object.keys(utils.existingComponents)
                    .filter(componentName => !scannedComponents.includes(componentName));
                if(!_.isEmpty(softDeletedComponents)) {
                    let sensorsToDetachByComponentName = {};
                    Object.values(utils.equipmentUnitComponents)
                    .flat()
                    .filter(component => softDeletedComponents.includes(component.name))
                    .forEach(component => {
                        sensorsToDetachByComponentName[component.name] = component.sensors
                    });
                    setUtils({ ...utils, softDeletedComponents: sensorsToDetachByComponentName });
                }
                else {
                    applyCategoryInconsistentCombinationQuery.refetch();
                }
            }
        }
    }

    const handleApplyButtonDisability = () => {
        allComponentInputs = { ...utils.typedInComponents, ...utils.dropDownSelectedComponents };
        return (Object.keys(allComponentInputs).length !== Object.keys(utils.categoryComponents).length) ||
            Object.values(allComponentInputs).filter(comp => _.isEmpty(comp)).length > 0;
    }

    const checkIfAnyApiRefetching = () =>
        fetchCategoriesQuery.isFetching ||
        applyCategoryQuery.isFetching ||
        applyCategoryInconsistentCombinationQuery.isFetching;

    const fetchCategories = async () => {
        const response = await getAllEquipmentUnitCategoriesByProfileId(profileId);
        return response?.data;
    }

    const applyCategoryInconsistentCombination = async () => {
        let payload = {
            equipmentUnitId: [props.id],
            categoryId: utils.selectedCategory.id,
            categoryAppliedComponentInfoList: [],
            isInconsistentScenarioApply: true,
            profileId: profileId
        },
        orderByComponentName = _.invert(utils.dropDownSelectedComponents);
        
        if(!_.isEmpty(utils.dropDownSelectedComponents)) {
            let dropDownSelectedComponentNames = Object.values(utils.dropDownSelectedComponents);
            Object.values(utils.equipmentUnitComponents)
            .flat()
            .filter(component => dropDownSelectedComponentNames.includes(component.name))
            .forEach(component => {
                payload.categoryAppliedComponentInfoList.push({
                    id: component.id,
                    order: orderByComponentName[component.name]
                })
            });
        }
        if(!_.isEmpty(utils.typedInComponents)) {
            Object.keys(utils.typedInComponents).forEach(componentOrder => {
                payload.categoryAppliedComponentInfoList.push(
                    {
                        name: utils.typedInComponents[componentOrder],
                        type: utils.categoryComponents[componentOrder],
                        order: componentOrder
                    }
                )
            })
        }

        let response = await applyCategoryToEquipmentUnit(payload);
        return response?.data;
    }

    const applyCategory = async () => {
        const response = await applyCategoryToEquipmentUnit({
            "equipmentUnitId": [props.id],
            "categoryId": utils.selectedCategory.id,
            "profileId": profileId
        });
        if(checkForAPISuccess(response?.data?.statusCode)) {
            setUtils({ ...utils, selectedCategory: {} });
            showSuccessToast("Category applied successfully");
            dispatch({
                type: UPDATE_CATEGORY_INFO,
                payload: {
                    equipmentUnitName: props.name,
                    categoryName: utils.selectedCategory.name,
                    equipmentId: props.equipmentId
                }
            });
            props.close();
        }
        else if(response?.data?.statusCode === RESPONSE_ERROR_IN_CATEGORY_APPLY_CODE) {
            const data = response?.data?.data;
            let componentIdByNames = {};
            if(!_.isEmpty(data?.equipmentUnitComponents)) {
                Object.values(data?.equipmentUnitComponents).flat().forEach(component => componentIdByNames[component.name] = component.id);
            }
            setUtils({
                ...utils, categoryComponents: data?.categoryComponents,
                equipmentUnitComponents: data?.equipmentUnitComponents,
                existingComponents: componentIdByNames
            });
        }
        else {
            showErrorToast("Failed to apply category");
        }
    }

    const applyCategoryInconsistentCombinationQuery = useQuery(
        'apply-category-inconsistent-combination',
        applyCategoryInconsistentCombination,
        { ...useQueryConfiguration }
    );

    const fetchCategoriesQuery = useQuery('get-categories', fetchCategories, { ...useQueryConfiguration });

    const applyCategoryQuery = useQuery(
        ['apply-category-single-equipment-unit', utils.selectedCategory.id],
        applyCategory,
        { ...useQueryConfiguration, enabled: !_.isEmpty(utils.selectedCategory?.id) }
    );

    useEffect(() => {
        if(fetchCategoriesQuery.isFetched) {
            if(checkForAPISuccess(fetchCategoriesQuery?.data?.statusCode)) {
                if(!_.isEmpty(fetchCategoriesQuery?.data?.data?.categories)) {
                    setUtils({ ...utils, categories: fetchCategoriesQuery?.data?.data?.categories });
                }
            }
            else {
                showErrorToast("Failed to fetch categories under this profile");
            }
            fetchCategoriesQuery.remove();
        }
        if(applyCategoryInconsistentCombinationQuery.isFetched) {
            if(checkForAPISuccess(applyCategoryInconsistentCombinationQuery?.data?.statusCode)) {
                dispatch({
                    type: UPDATE_CATEGORY_INFO,
                    payload: {
                        newComponents: applyCategoryInconsistentCombinationQuery?.data?.data?.newComponents,
                        softDeletedComponentNames: Object.keys(utils.softDeletedComponents),
                        equipmentUnitName: props.name, categoryName: utils.selectedCategory.name, equipmentId: props.equipmentId
                    }
                });
                showSuccessToast("Category applied successfully");
                props.close();
            }
            else {
                showErrorToast("Failed to apply category.");
            }
            applyCategoryInconsistentCombinationQuery.remove();
        }
    }, [fetchCategoriesQuery.isFetched, applyCategoryInconsistentCombinationQuery.isFetched]);

    useEffect(() => {
        fetchCategoriesQuery.refetch();
        dispatch({ type: DESELECT_COMPONENT });
    }, []);

    return (
        <Modal backdrop="static" show onHide={() => props.close()}>
            <Modal.Header closeButton>
                <h5 className="font-weight-bold">
                    Apply category
                </h5>
            </Modal.Header>
            <Modal.Body className="w-95">
                {
                    checkIfAnyApiRefetching() ?
                        <div className="text-center my-5">
                            <ClipLoader className="" color="#075978" />
                        </div>
                            :
                        !_.isEmpty(utils.softDeletedComponents) ?
                            <div className="my-4">
                                <span style={{ fontSize: "16px" }}>Following components will be deleted and sensor under it will be detached</span>
                                {
                                    Object.keys(utils.softDeletedComponents).map(componentName => 
                                
                                        <div className="my-3">
                                            <div>
                                                <b>Component :</b> <span className="ml-1">{componentName}</span>
                                            </div>
                                            <div>
                                                <b>Sensor(s) :</b>
                                                <span className="ml-2">
                                                    {
                                                        !_.isEmpty(utils.softDeletedComponents[componentName]) ?
                                                            utils.softDeletedComponents[componentName].join(", ")
                                                                :
                                                            "-"
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                                :
                            <>
                                {
                                    !_.isEmpty(utils.invalidTypedInComponentNames) &&
                                        <div className="my-3 color-red">
                                            The name(s) 
                                            <span className="font-weight-bold">
                                                {" " + utils.invalidTypedInComponentNames.join(", ") + " "}
                                            </span>
                                            is already taken.
                                            Try giving different name for new component
                                        </div>
                                }
                                {
                                    !_.isEmpty(utils.duplicateComponentNames) &&
                                        <div className="my-3 color-red">
                                            The component(s) 
                                            <span className="font-weight-bold">
                                                {" " + utils.duplicateComponentNames.join(", ") + " "}
                                            </span>
                                            is selected multiple times.
                                        </div>
                                }
                                {
                                    !_.isEmpty(utils.categories) ?
                                        <PerfectScrollbar style={{ height: "35vh" }}>
                                            {
                                                !_.isEmpty(utils.categoryComponents) ?
                                                    <div className="w-100">
                                                        {
                                                            Object.keys(utils.categoryComponents).map(componentOrder =>
                                                                <div style={{ borderBottom: "1px solid #eaeaea" }} className="d-flex my-2">
                                                                    <div style={{ width: "35%" }}>
                                                                        <div> Order </div>
                                                                        <div className="my-2"> Type </div>
                                                                        <div> Component </div>
                                                                    </div>
                                                                    <div style={{ width: "65%" }}>
                                                                        <div> {componentOrder} </div>
                                                                        <div className="my-2">
                                                                            {utils.categoryComponents[componentOrder]}
                                                                        </div>
                                                                        {
                                                                            checkIfComponentsAvailableForSelection(componentOrder) &&
                                                                            !Object.keys(utils.typedInComponents)?.includes(componentOrder) ?
                                                                                <select
                                                                                    onChange={(e) => 
                                                                                        handleComponentSelect(
                                                                                            e.target.value,
                                                                                            componentOrder
                                                                                        )
                                                                                    }
                                                                                    className="category-component-select mb-2"
                                                                                >
                                                                                    <option selected hidden disabled>Select</option>
                                                                                    {
                                                                                        utils.equipmentUnitComponents
                                                                                        [utils.categoryComponents[componentOrder]]
                                                                                        .map((component, ind) =>
                                                                                            <option
                                                                                                value={
                                                                                                    utils.equipmentUnitComponents
                                                                                                    [utils.categoryComponents[componentOrder]]
                                                                                                    [ind].name
                                                                                                }
                                                                                            >
                                                                                                {component.name}
                                                                                            </option>
                                                                                        )
                                                                                    }
                                                                                    <option value={componentOrder}>Create new component</option>
                                                                                </select>
                                                                                    :
                                                                                <>
                                                                                    <input
                                                                                        value={
                                                                                            !_.isEmpty(utils.typedInComponents?.[componentOrder]) ?
                                                                                                utils.typedInComponents[componentOrder]
                                                                                                    :
                                                                                                ""
                                                                                        }
                                                                                        onChange={
                                                                                            (e) =>
                                                                                                handleNewComponentInput(
                                                                                                    e.target.value,
                                                                                                    componentOrder
                                                                                                )
                                                                                        }
                                                                                        placeholder="Provide component name"
                                                                                        className="mb-2 category-component-input"
                                                                                    />
                                                                                    <Icon
                                                                                        icon={'feather-x'}
                                                                                        onClick={() => handleNewComponentInputClose(componentOrder)}
                                                                                        width={18}
                                                                                        height={18}
                                                                                        className="cursor-pointer ml-1"
                                                                                    />
                                                                                </>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                        :
                                                    utils.categories.map((category) => 
                                                        <div
                                                            onClick={
                                                                () => 
                                                                    setUtils({
                                                                        ...utils,
                                                                        selectedCategory: { id: category.id, name: category.name } 
                                                                    })
                                                            }
                                                            className="apply-category-item" key={category.id}
                                                        >
                                                            <div className="my-1">
                                                                {category.name}
                                                            </div>
                                                            <div className="text-color-grey">
                                                                {category.components}
                                                            </div>
                                                        </div>
                                                    )
                                            }
                                        </PerfectScrollbar>
                                            :
                                        <center className="my-5 font-weight-bold">
                                            No categories found for this profile. Please create one before applying.
                                        </center>
                                }
                            </>                    
                }
            </Modal.Body>
            {
                !_.isEmpty(utils.categoryComponents) &&
                    <Modal.Footer style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className="my-2 font-weight-bold">
                            {
                                !_.isEmpty(utils.softDeletedComponents) ?
                                    "Click on yes to confirm the action"
                                        :
                                    ""
                            }
                        </div>
                        <div>
                            <Button 
                                disabled={checkIfAnyApiRefetching()}
                                onClick={() => props.close()}
                                className="secondary-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={handleApplyButtonDisability() || checkIfAnyApiRefetching()}
                                onClick={
                                    () => !_.isEmpty(utils.softDeletedComponents) ?
                                        applyCategoryInconsistentCombinationQuery.refetch()
                                            :
                                        handleApplyValidation()
                                }
                            >
                                {!_.isEmpty(utils.categoryComponents) ? "Yes" : "Apply"}
                            </Button>
                        </div>
                    </Modal.Footer>
            }
        </Modal>
    )
}

export default ApplyCategoryToEquipmentUnit;